<script setup lang="ts">
import { computed } from 'vue';

import { useBrowserStore } from '@apps/main/new-desktop/core/stores';

import type { AppSvgIconColor } from '@libs/ui';
import { AppRawButton, AppSvgIcon } from '@libs/ui';

import type { CurrentMonthUnsearchedDayTile } from '../models';
import { TileSelectionType } from '../models';

const browserStore = useBrowserStore();

interface CurrentMonthUnsearchedDayTileViewProps {
  calendarTile: CurrentMonthUnsearchedDayTile;
  showPrices: boolean;
  allowSelection: boolean;
}

interface CurrentMonthUnsearchedDayTileViewEmits {
  (eventName: 'select-date', date: Date): void;
}

const emit = defineEmits<CurrentMonthUnsearchedDayTileViewEmits>();

const props = defineProps<CurrentMonthUnsearchedDayTileViewProps>();

const iconColor = computed<AppSvgIconColor>(() =>
  props.calendarTile.selection === TileSelectionType.Selected
    ? 'color-white'
    : 'color-neutral-70'
);

function onClick(): void {
  if (props.allowSelection) {
    emit('select-date', props.calendarTile.date);
  }
}
</script>

<template>
  <AppRawButton
    class="current-month-unsearched-day-tile-view"
    :class="{
      selected: props.calendarTile.selection === TileSelectionType.Selected,
      'allow-hover': props.allowSelection,
    }"
    @click="onClick"
  >
    {{ props.calendarTile.text }}
    <div v-if="props.showPrices" class="price-placeholder">
      <AppSvgIcon
        name="lookingGlass"
        :size="browserStore.isSmallerThan600px ? '13' : '18'"
        :color="iconColor"
      />
    </div>
  </AppRawButton>
</template>

<style scoped lang="scss">
@use '../styles/price_calendar_mixins' as *;

.current-month-unsearched-day-tile-view {
  width: var(--calendar-tile-size);
  height: var(--calendar-tile-size);
  border-radius: var(--calendar-tile-border-radius);
  background-color: var(--card-tile-background-color);
  transition: background-color var(--transition-short);
  @include calendar-tile-day-font;
  color: var(--calendar-tile-day-font-color);
  cursor: default;

  display: flex;
  flex-direction: column;
  gap: var(--calendar-tile-day-price-gap);
  align-items: center;
  justify-content: center;

  position: relative;

  &.allow-hover {
    @include on-hover {
      background-color: var(--calendar-tile-hover-color);
      cursor: pointer;
    }
  }

  &.selected {
    background-color: var(--color-neutral-70);
    color: var(--color-white);

    @include on-hover {
      background-color: var(--color-neutral-70);
    }
  }

  .price-placeholder {
    height: var(--calendar-price-placeholder-height);
  }
}
</style>
